import { supabase } from '@/integrations/supabase/client';
import type { Database, Json } from '@/integrations/supabase/types';

/**
 * Direct REST endpoint constants. Using bare fetch() instead of the Supabase
 * SDK for analytics inserts keeps the Supabase client out of any
 * critical-path module graph that PageSpeed sees as a "blocking request".
 * Lighthouse's network dependency tree was attributing 3+ s of fake critical
 * latency to these inserts even though they ran after window.load.
 */
const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SB_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const REST_HEADERS = {
  apikey: SB_KEY,
  Authorization: `Bearer ${SB_KEY}`,
  'Content-Type': 'application/json',
  Prefer: 'return=minimal',
};

function postBeacon(table: string, payload: unknown): Promise<boolean> {
  // sendBeacon would be ideal but it sets Content-Type to text/plain which
  // PostgREST rejects. We use fetch with keepalive: true which behaves the
  // same (fire-and-forget, survives unload) and lets us set headers.
  try {
    return fetch(`${SB_URL}/rest/v1/${table}`, {
      method: 'POST',
      headers: REST_HEADERS,
      body: JSON.stringify(payload),
      keepalive: true,
    }).then((r) => r.ok).catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

const VISITOR_KEY = 'bf_visitor_id';
const SESSION_KEY = 'bf_session_id';
const SESSION_TS_KEY = 'bf_session_ts';
const SESSION_TIMEOUT = 30 * 60 * 1000;
const SKIP_DOMAINS = /localhost|\.lovable\.app$|\.lovableproject\.com$|\.webcontainer\./i;

// Bot detection patterns matching common crawlers, SEO tools, headless browsers, and AI crawlers
const BOT_UA = /bot|crawl|spider|slurp|bingpreview|mediapartners|facebookexternalhit|linkedinbot|twitterbot|whatsapp|telegram|semrush|ahrefs|mj12bot|dotbot|petalbot|yandex|baidu|googlebot|sogou|360spider|bytespider|headlesschrome|phantomjs|puppeteer|selenium|playwright|lighthouse|pingdom|uptimerobot|gtmetrix|pagespeed|chrome-lighthouse|chatgpt|gptbot|oai-searchbot|claudebot|claude-web|anthropic|perplexity|cohere|ccbot|amazonbot|applebot|duckduckbot|youbot|node-fetch|axios|python-requests|libwww|scrapy|httpx|undici|java\/|okhttp/i;

function isBotBrowser(): boolean {
  try {
    const ua = navigator.userAgent || '';
    if (!ua || ua.length < 20) return true;
    if (BOT_UA.test(ua)) return true;
    // Headless Chrome / common automation flags
    if (/HeadlessChrome/i.test(ua)) return true;
    if ((navigator as { webdriver?: boolean }).webdriver) return true;
    // Headless typically has 0 plugins and 0 languages
    if (Array.isArray(navigator.languages) && navigator.languages.length === 0) return true;
    // Window dimensions of 0 → headless
    if (window.outerWidth === 0 && window.outerHeight === 0) return true;
    // Generic curl-like UA that real browsers never send
    if (/^Mozilla\/5\.0$/i.test(ua.trim())) return true;
    // Specific automation globals leaked by Puppeteer/Playwright
    const w = window as unknown as Record<string, unknown>;
    if (w.__nightmare || w.__phantomas || w.callPhantom || w._phantom) return true;
    return false;
  } catch {
    return false;
  }
}

type AnalyticsSessionInsert = Database['public']['Tables']['analytics_sessions']['Insert'];
type AnalyticsEventInsert = Database['public']['Tables']['analytics_events']['Insert'];
type AnalyticsPageViewInsert = Database['public']['Tables']['analytics_page_views']['Insert'];

function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
  };
}

function getReferrerSource(): string {
  const ref = document.referrer;
  if (!ref) return 'direct';
  try {
    const url = new URL(ref);
    if (url.hostname === window.location.hostname) return '';
    if (/google\./i.test(url.hostname)) return 'google';
    if (/bing\./i.test(url.hostname)) return 'bing';
    if (/facebook|fb\./i.test(url.hostname)) return 'facebook';
    if (/instagram/i.test(url.hostname)) return 'instagram';
    if (/tiktok/i.test(url.hostname)) return 'tiktok';
    if (/twitter|x\.com/i.test(url.hostname)) return 'twitter';
    if (/youtube/i.test(url.hostname)) return 'youtube';
    if (/linkedin/i.test(url.hostname)) return 'linkedin';
    return url.hostname;
  } catch {
    return ref;
  }
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  if (/mobile|iphone|android(?!.*tablet)/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (/edg\//i.test(ua)) return 'Edge';
  if (/chrome/i.test(ua) && !/chromium/i.test(ua)) return 'Chrome';
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
  if (/opera|opr/i.test(ua)) return 'Opera';
  return 'Other';
}

export function shouldTrackAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  if (SKIP_DOMAINS.test(window.location.hostname) && window.location.hostname !== 'bigfollow.lovable.app') return false;

  // Bot / headless browser detection
  if (isBotBrowser()) return false;

  try {
    const raw = localStorage.getItem('admin_2fa_verified');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.uid && parsed?.ts) return false;
    }
  } catch {
    // ignore storage parsing errors
  }

  return true;
}

export function getCurrentAnalyticsSessionId(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

export function touchAnalyticsSession(): void {
  sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()));
}

function getStoredAnalyticsSessionId(): string | null {
  const existing = sessionStorage.getItem(SESSION_KEY);
  const lastTs = Number(sessionStorage.getItem(SESSION_TS_KEY) || '0');

  if (existing && Date.now() - lastTs < SESSION_TIMEOUT) {
    touchAnalyticsSession();
    return existing;
  }

  return null;
}

let _sessionPromise: Promise<string | null> | null = null;

export async function ensureAnalyticsSession(): Promise<string | null> {
  if (!shouldTrackAnalytics()) return null;

  const existing = getStoredAnalyticsSessionId();
  if (existing) return existing;

  // Deduplicate concurrent calls
  if (_sessionPromise) return _sessionPromise;

  _sessionPromise = (async () => {
    const sessionId = crypto.randomUUID();
    const utm = getUtmParams();

    const payload: AnalyticsSessionInsert = {
      id: sessionId,
      visitor_id: getVisitorId(),
      referrer: getReferrerSource(),
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      landing_page: window.location.pathname,
      device_type: getDeviceType(),
      browser: getBrowser(),
      country: '',
      city: '',
    };

    const ok = await postBeacon('analytics_sessions', payload);
    if (!ok) {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_TS_KEY);
      return null;
    }
    sessionStorage.setItem(SESSION_KEY, sessionId);
    touchAnalyticsSession();
    return sessionId;
  })();

  try {
    return await _sessionPromise;
  } finally {
    _sessionPromise = null;
  }
}

export async function trackAnalyticsEvent(eventType: string, metadata?: Record<string, unknown>): Promise<void> {
  const sessionId = await ensureAnalyticsSession();
  if (!sessionId) return;

  touchAnalyticsSession();
  const payload: AnalyticsEventInsert = {
    session_id: sessionId,
    event_type: eventType,
    metadata: (metadata || {}) as Json,
  };

  await postBeacon('analytics_events', payload);
}

export async function trackAnalyticsPageView(path: string, title: string): Promise<void> {
  const sessionId = await ensureAnalyticsSession();
  if (!sessionId) return;

  touchAnalyticsSession();
  const payload: AnalyticsPageViewInsert = {
    session_id: sessionId,
    path,
    title: title || '',
  };

  // Both calls use keepalive fetch — fire-and-forget, no awaiting needed for
  // user-perceived performance, but we await to keep call ordering intact.
  await postBeacon('analytics_page_views', payload);

  // RPC for page-count increment via PostgREST.
  try {
    await fetch(`${SB_URL}/rest/v1/rpc/increment_session_page_count`, {
      method: 'POST',
      headers: REST_HEADERS,
      body: JSON.stringify({ sid: sessionId }),
      keepalive: true,
    });
  } catch {
    // ignore — non-critical
  }
}